/**
 * Company roles and permissions over HTTP (SC-439). The routes live in `server/routes/rbac.ts`;
 * every call sends `Authorization: Bearer <Parse session token>`.
 *
 * The server answers errors as `{ success: false, error, ... }` and refusals as 403
 * `{ success: false, error: 'forbidden', reason, permission? }`. Success bodies are the data,
 * with no `success` field. This module imports nothing from the stores, so the stores can use it
 * without a cycle.
 */
import Parse from 'parse'
import type { PermissionKey } from '../../../types/rbac/catalogue'
import { getServerBaseUrl } from '../utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'

/** What a surface shows as "this unlocks ...", from the catalogue endpoint. */
export interface RbacUnlock {
  id: string
  label: string
  kind: string
}

export interface RbacCataloguePermission {
  key: PermissionKey
  group: string
  label: string
  description: string
  grantable: boolean
  ownerOnly: boolean
  reserved: boolean
  isActive: boolean
  unlocks: RbacUnlock[]
}

export interface RbacSystemRole {
  key: string
  name: string
  description: string
  permissions: string[]
}

export interface RbacCatalogue {
  groups: string[]
  permissions: RbacCataloguePermission[]
  systemRoles: RbacSystemRole[]
}

/** `GET /api/rbac/me`: what the signed-in person may do in one company. */
export interface RbacMe {
  companyId: string
  roleKey: string | null
  permissions: string[]
  isOwner: boolean
  isPlatformAdmin: boolean
  /** False while cloud still applies the legacy role rules (the switch is off). */
  enforced: boolean
}

export interface RbacRole {
  key: string
  name: string
  description: string
  isSystem: boolean
  scope: 'global' | 'company'
  permissions: string[]
}

export interface RbacOverride {
  key: string
  isAllowed: boolean
}

/** `GET` and `PUT /api/companies/:companyId/members/:userId/access`. */
export interface MemberAccess {
  userId: string
  roleKey: string | null
  overrides: RbacOverride[]
  effective: string[]
  roleDefaults: string[]
  canEditRole: boolean
  canEditPermissions: boolean
  lockedKeys: string[]
  reasonIfLocked?: string
}

/** `isAllowed: null` clears the override; a key that is not listed stays as it is. */
export interface MemberAccessPatch {
  roleKey?: string
  overrides?: Array<{ key: string; isAllowed: boolean | null }>
}

export interface OwnershipTransferResult {
  ownerUserId: string
  previousOwnerUserId: string
}

/** One of a company's Owners, from the platform owner-management API. */
export interface CompanyOwner {
  userId: string
  displayName: string
  email: string
  /** False for an Owner who left or was deactivated; they still count as an Owner on the list. */
  isActive: boolean
}

/** `PUT /api/admin/rbac/companies/:companyId/owner`: the new sole Owner and who they replaced. */
export interface SoleOwnerResult {
  ownerUserId: string
  previousOwnerUserId: string | null
  /** Every Owner who was demoted, as the server lists them (older servers omit it). */
  previousOwnerUserIds?: string[]
}

/** Refusal reasons the server sends today; others are handled generically by the message code. */
export type RbacRefusalReason =
  | 'missing_permission'
  | 'not_held'
  | 'target_holds_more'
  | 'self_edit'
  | 'owner_target'
  | 'not_a_member'

export class RbacApiError extends Error {
  readonly status: number
  /** 403 only: why the server refused. May be a reason this client has never seen. */
  readonly reason?: string
  /** 403 only: the permission key involved, when there is one. */
  readonly permission?: string
  /** 400 and 404: a stable code such as `member_not_found`. */
  readonly code?: string

  constructor(
    status: number,
    message: string,
    extra: { reason?: string; permission?: string; code?: string } = {}
  ) {
    super(message)
    this.name = 'RbacApiError'
    this.status = status
    this.reason = extra.reason
    this.permission = extra.permission
    this.code = extra.code
  }
}

export function isRbacApiError(error: unknown): error is RbacApiError {
  return error instanceof RbacApiError
}

/** What a failed call says when the server sent no sentence of its own. */
export const fallbackFailureMessage = (status: number) => `Request failed (${status})`

function sessionToken(): string {
  return getStoredSessionToken() || Parse.User.current()?.getSessionToken() || ''
}

async function request<T>(
  method: 'GET' | 'PUT' | 'POST',
  path: string,
  body?: Record<string, unknown>
): Promise<T> {
  const response = await fetch(`${getServerBaseUrl()}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${sessionToken()}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await response.json().catch(() => null)
  if (!response.ok) {
    const payload = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
    const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
    throw new RbacApiError(
      response.status,
      text(payload.error) ?? fallbackFailureMessage(response.status),
      {
        reason: text(payload.reason),
        permission: text(payload.permission),
        code: text(payload.code),
      }
    )
  }
  return data as T
}

const company = (companyId: string) => `/api/companies/${encodeURIComponent(companyId)}`
const member = (companyId: string, userId: string) =>
  `${company(companyId)}/members/${encodeURIComponent(userId)}/access`

export const fetchMyAccess = (companyId: string) =>
  request<RbacMe>('GET', `/api/rbac/me?companyId=${encodeURIComponent(companyId)}`)

export const fetchCatalogue = () => request<RbacCatalogue>('GET', '/api/rbac/catalogue')

export const fetchCompanyRoles = async (companyId: string) =>
  (await request<{ roles: RbacRole[] }>('GET', `${company(companyId)}/roles`)).roles

/** `GET /api/companies/:companyId/roles` as the invite screens read it. */
export interface CompanyRolesResult {
  roles: RbacRole[]
  /**
   * The company's effective default role key (company default, then platform default, then
   * Member). Absent from a server that predates it; callers fall back to
   * `Company.settings.default_role_key`, then `member`.
   */
  defaultRoleKey?: string
}

const rolesInFlight = new Map<string, Promise<CompanyRolesResult>>()

/**
 * The roles list plus the default role, for the invite dialog, the workspace link card and the
 * default-role picker. Callers that open together (the onboarding step and its link card) share
 * one request; nothing is kept once it settles, so a failure is never cached and the next open
 * asks again.
 */
export function fetchCompanyRolesWithDefault(companyId: string): Promise<CompanyRolesResult> {
  const running = rolesInFlight.get(companyId)
  if (running) return running
  const call = request<{ roles?: RbacRole[]; defaultRoleKey?: unknown }>(
    'GET',
    `${company(companyId)}/roles`
  )
    .then((body) => ({
      roles: Array.isArray(body.roles) ? body.roles : [],
      ...(typeof body.defaultRoleKey === 'string' && body.defaultRoleKey
        ? { defaultRoleKey: body.defaultRoleKey }
        : {}),
    }))
    .finally(() => {
      rolesInFlight.delete(companyId)
    })
  rolesInFlight.set(companyId, call)
  return call
}

export const fetchMemberAccess = (companyId: string, userId: string) =>
  request<MemberAccess>('GET', member(companyId, userId))

export const saveMemberAccess = (companyId: string, userId: string, patch: MemberAccessPatch) =>
  request<MemberAccess>('PUT', member(companyId, userId), patch as Record<string, unknown>)

export const transferOwnership = (companyId: string, newOwnerUserId: string) =>
  request<OwnershipTransferResult>('POST', `${company(companyId)}/ownership-transfer`, {
    newOwnerUserId,
  })

// Platform admins only (SC-439): the company comes from the path, never from the body.
const adminOwners = (companyId: string) =>
  `/api/admin/rbac/companies/${encodeURIComponent(companyId)}`

export const fetchCompanyOwners = async (companyId: string) =>
  (await request<{ owners: CompanyOwner[] }>('GET', `${adminOwners(companyId)}/owners`)).owners

export const addCompanyOwner = async (companyId: string, userId: string) =>
  (
    await request<{ owners: CompanyOwner[] }>('POST', `${adminOwners(companyId)}/owners`, {
      userId,
    })
  ).owners

/** `roleKey` is the demoted Owner's new role; left out, the server uses Admin. */
export const demoteCompanyOwner = async (companyId: string, userId: string, roleKey?: string) =>
  (
    await request<{ owners: CompanyOwner[] }>(
      'POST',
      `${adminOwners(companyId)}/owners/${encodeURIComponent(userId)}/demote`,
      roleKey ? { roleKey } : {}
    )
  ).owners

/** Every other Owner gets `previousOwnersRoleKey`; left out, the server uses Admin. */
export const makeSoleCompanyOwner = (
  companyId: string,
  userId: string,
  previousOwnersRoleKey?: string
) =>
  request<SoleOwnerResult>('PUT', `${adminOwners(companyId)}/owner`, {
    userId,
    ...(previousOwnersRoleKey ? { previousOwnersRoleKey } : {}),
  })
